import React, { useState, useEffect, useRef } from 'react';
import { Book, Plus, Edit2, Trash2, Save, X, Tag, Upload } from 'lucide-react';

interface KnowledgeEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

const CATEGORIES = ['products', 'services', 'pricing', 'faq', 'policies'];

export default function KnowledgeBasePage() {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('products');
  const [formTags, setFormTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const response = await fetch('/api/ai/knowledge?api_key=synapse-dev');
      const data = await response.json();
      if (data.success) {
        setEntries(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load knowledge base:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry);
    setFormTitle(entry.title);
    setFormContent(entry.content);
    setFormCategory(entry.category);
    setFormTags(entry.tags.join(', '));
    setIsCreating(false);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingEntry(null);
    setFormTitle('');
    setFormContent('');
    setFormCategory('products');
    setFormTags('');
    setUploadedFilename('');
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setIsCreating(false);
    setFormTitle('');
    setFormContent('');
    setFormCategory('products');
    setFormTags('');
    setUploadedFilename('');
  };

  const saveEntry = async () => {
    const tags = formTags.split(',').map(t => t.trim()).filter(t => t);

    try {
      if (isCreating) {
        // Create new entry
        const response = await fetch('/api/ai/knowledge?api_key=synapse-dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: formTitle,
            content: formContent,
            category: formCategory,
            tags
          })
        });

        const data = await response.json();
        if (data.success) {
          await loadEntries();
          cancelEdit();
        }
      } else if (editingEntry) {
        // Update existing entry
        const response = await fetch(`/api/ai/knowledge/${editingEntry.id}?api_key=synapse-dev`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: formTitle,
            content: formContent,
            category: formCategory,
            tags
          })
        });

        const data = await response.json();
        if (data.success) {
          await loadEntries();
          cancelEdit();
        }
      }
    } catch (error) {
      console.error('Failed to save entry:', error);
    }
  };

  const deleteEntry = async (id: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      const response = await fetch(`/api/ai/knowledge/${id}?api_key=synapse-dev`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        await loadEntries();
      }
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const formData = new FormData();

    // Add all files to form data
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const response = await fetch('/api/ai/knowledge/upload?api_key=synapse-dev', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success && data.results && data.results.length > 0) {
        // Show summary
        const summary = `Processed ${data.processed} file(s)${data.failed > 0 ? `, ${data.failed} failed` : ''}`;

        // If single file, populate form
        if (data.results.length === 1) {
          const result = data.results[0];
          setIsCreating(true);
          setEditingEntry(null);
          setFormTitle(result.filename.replace(/\.[^/.]+$/, ''));
          setFormContent(result.content);
          setFormCategory('products');
          setFormTags('');
          setUploadedFilename(result.fromZip ? `${result.filename} (from ${result.fromZip})` : result.filename);
        } else {
          // Multiple files - create entries directly
          let successCount = 0;
          for (const result of data.results) {
            try {
              const title = result.filename.replace(/\.[^/.]+$/, '');
              const res = await fetch('/api/ai/knowledge?api_key=synapse-dev', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title,
                  content: result.content,
                  category: 'products',
                  tags: result.fromZip ? ['imported', 'from-zip'] : ['imported']
                })
              });
              if (res.ok) successCount++;
            } catch (err) {
              console.error(`Failed to save ${result.filename}:`, err);
            }
          }
          await loadEntries();
          alert(`${summary}\n${successCount} entries created successfully`);
        }

        if (data.errors && data.errors.length > 0) {
          console.warn('Upload errors:', data.errors);
        }
      } else {
        alert(`Upload failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const filteredEntries = selectedCategory === 'all'
    ? entries
    : entries.filter(e => e.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      products: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      services: 'bg-green-500/20 text-green-300 border-green-500/30',
      pricing: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      faq: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      policies: 'bg-orange-500/20 text-orange-300 border-orange-500/30'
    };
    return colors[category] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0e14] via-[#0d1117] to-[#161b22] text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Book className="w-8 h-8 text-purple-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Knowledge Base
            </h1>
          </div>
          <p className="text-gray-400">
            Manage AI assistant knowledge - products, services, pricing, FAQs, and policies
          </p>
        </div>

        {/* Category Filter + Add Button */}
        <div className="flex gap-3 mb-6 flex-wrap items-center">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-purple-500/30 text-purple-100 border border-purple-500/50'
                : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:border-purple-500/30'
            }`}
          >
            All
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                selectedCategory === cat
                  ? getCategoryColor(cat) + ' border'
                  : 'bg-[#161b22]/30 text-gray-400 border border-[#30363d] hover:border-purple-500/30'
              }`}
            >
              {cat}
            </button>
          ))}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.zip"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="ml-auto px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600
                     hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg
                     flex items-center gap-2 text-sm font-medium transition-all
                     shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>

          <button
            onClick={startCreate}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600
                     hover:from-purple-500 hover:to-pink-500 text-white rounded-lg
                     flex items-center gap-2 text-sm font-medium transition-all
                     shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
        </div>

        {/* Edit Form */}
        {(editingEntry || isCreating) && (
          <div className="bg-[#161b22]/50 backdrop-blur-xl border border-purple-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-purple-300">
                  {isCreating ? 'Create New Entry' : 'Edit Entry'}
                </h2>
                {uploadedFilename && (
                  <p className="text-xs text-gray-400 mt-1">
                    From file: {uploadedFilename}
                  </p>
                )}
              </div>
              <button
                onClick={cancelEdit}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Entry title..."
                  className="w-full px-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                           text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                           text-white focus:border-purple-500 focus:outline-none capitalize"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat} className="capitalize">{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Content</label>
                <textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder="Entry content (markdown supported)..."
                  rows={12}
                  className="w-full px-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                           text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none
                           font-mono text-sm resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  placeholder="hardwood, pricing, installation..."
                  className="w-full px-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                           text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={saveEntry}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600
                           hover:from-purple-500 hover:to-pink-500 text-white rounded-lg
                           flex items-center gap-2 font-medium transition-all
                           shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-6 py-2 bg-[#21262d] hover:bg-[#30363d] text-gray-300
                           rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Entries List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No entries found. Click "Add Entry" to create one.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map(entry => (
              <div
                key={entry.id}
                className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d]
                         hover:border-purple-500/30 rounded-lg p-5 transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{entry.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getCategoryColor(entry.category)}`}>
                        {entry.category}
                      </span>
                    </div>
                    {entry.tags && entry.tags.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Tag className="w-3 h-3 text-gray-500" />
                        {entry.tags.map((tag, i) => (
                          <span key={i} className="text-xs text-gray-400">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(entry)}
                      className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300
                               rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300
                               rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-300 whitespace-pre-wrap font-mono bg-[#0d1117]/50
                              p-3 rounded border border-[#21262d] max-h-48 overflow-y-auto">
                  {entry.content}
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Updated: {new Date(entry.updated_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
